import { useEffect, useId, useRef, useState } from "react"
import { Loader2, Send } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"
import { useLlmOptions } from "../hooks/use-llm-options"
import { useKnowledgeBases } from "@/pages/knowledge-bases/presentation/hooks/use-knowledge-bases"
import { SynthesisSessionSettingsFields } from "./synthesis-session-settings-fields"

const composerTextareaClass =
  "min-h-10 max-h-16 w-full flex-1 resize-none overflow-y-auto [field-sizing:fixed] md:text-sm"

const kbdHint =
  "Enter sends your message. Shift+Enter starts a new line."
const disclaimer =
  "AI can make mistakes. Please double-check cited sources"

export type NewSessionConfiguration = {
  model: string
  knowledgeBaseIds: string[]
  enableWebSearch: boolean
}

type SynthesisStartConversationProps = {
  value: string
  onChange: (value: string) => void
  onStart: (config: NewSessionConfiguration) => void
  busy: boolean
  isCreating: boolean
}

export const SynthesisStartConversation = ({
  value,
  onChange,
  onStart,
  busy,
  isCreating,
}: SynthesisStartConversationProps) => {
  const kbdHintId = useId()
  const disclaimerId = useId()

  const { data: llmOptions, isLoading: llmLoading } = useLlmOptions()
  const { data: kbData, isLoading: kbLoading } = useKnowledgeBases({
    page: 1,
    limit: 100,
  })
  const kbs = kbData?.items ?? []
  const options = llmOptions ?? []
  const settingsLoading = llmLoading || kbLoading

  const [model, setModel] = useState("")
  const [kbIds, setKbIds] = useState<string[]>([])
  const submittingRef = useRef(false)
  useEffect(() => {
    if (!busy) submittingRef.current = false
  }, [busy])

  const currentConfig = (): NewSessionConfiguration => ({
    model,
    knowledgeBaseIds: kbIds,
    enableWebSearch: false,
  })

  const handleStart = () => {
    if (submittingRef.current || busy || !value.trim()) return
    submittingRef.current = true
    onStart(currentConfig())
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col md:flex-row">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-4 py-8">
          {isCreating ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <Spinner className="size-10" />
              <p className="text-sm text-muted-foreground">
                Starting conversation…
              </p>
            </div>
          ) : (
            <div className="w-full max-w-lg space-y-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                Information Synthesis
              </h1>
              <p className="text-left text-xs text-muted-foreground">
                Choose model, knowledge bases, and web search on the right; they
                apply when you send your first message. You can change them
                later from the conversation settings.
              </p>
            </div>
          )}
        </div>

        <div
          className={cn(
            "sticky bottom-0 z-10 shrink-0  p-3",
            "bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur-sm",
          )}
        >
          <div className="mx-auto w-full max-w-lg">
            <p
              id={kbdHintId}
              className="mb-1.5 text-xs leading-snug text-muted-foreground"
            >
              {kbdHint}
            </p>
            <div className="flex items-end gap-2">
              <Textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="What would you like to know?"
                rows={2}
                className={composerTextareaClass}
                disabled={busy}
                aria-describedby={`${kbdHintId} ${disclaimerId}`}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !e.shiftKey &&
                    !e.nativeEvent.isComposing
                  ) {
                    e.preventDefault()
                    handleStart()
                  }
                }}
              />
              <Button
                type="button"
                size="icon"
                className="size-9 shrink-0"
                disabled={busy || !value.trim()}
                onClick={handleStart}
                aria-label={
                  busy
                    ? isCreating
                      ? "Starting"
                      : "Getting reply"
                    : "Start conversation"
                }
              >
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
              </Button>
            </div>
            <p
              id={disclaimerId}
              className="mt-1.5 text-xs leading-snug text-muted-foreground"
            >
              {disclaimer}
            </p>
          </div>
        </div>
      </div>

      <aside
        className={cn(
          "flex min-h-0 max-h-[45vh] w-full shrink-0 flex-col border-t bg-background md:max-h-none md:max-w-sm md:border-t-0 md:border-l",
        )}
        aria-label="Conversation settings"
      >
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <h2 className="mb-4 text-lg font-semibold tracking-tight">Settings</h2>
          {settingsLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="size-8" />
            </div>
          ) : (
            <SynthesisSessionSettingsFields
              idPrefix="synth-start"
              model={model}
              onModelChange={setModel}
              kbIds={kbIds}
              onKbIdsChange={setKbIds}
              llmOptions={options}
              kbs={kbs}
              enableForm={true}
            />
          )}
        </div>
      </aside>
    </div>
  )
}
